import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useFilters from '../useFilters';
import AnalyticsApi from 'libs/api/analytics';
import Helpers from 'helpers';
import UIHelper from 'helpers/UIHelper';
import { Table } from 'antd';
import Text from 'antd/es/typography/Text';
import { usePdf } from '../../../../hooks/usePdf';
import moment from 'moment';
import { fileNameEdit } from 'helpers/FileUtils';
import { useRecoilValue } from 'recoil';
import { getShoppingCenters } from '../../../../hooks/useUser';

const api = new AnalyticsApi('vip_club/');

const format = (v) => `${v}%`;

const defaultState = {
  tableData: [],
  lineChartData: [],
  barChart: [],
  barChangeLine: [],
  total: {},
};

const useVipClub = (isPrint, allReportsData, isAllReports) => {
  const { t } = useTranslation();
  const shoppingCenters = useRecoilValue(getShoppingCenters);
  const { handleDownloadPdf, loadingPdf, createCustomEvent } = usePdf();
  const [errors, setErrors] = useState([]);
  const { title, searchParams } = useFilters(false, true, isAllReports);
  const [loading, setLoading] = useState(false);

  const [allData, setAllData] = useState({
    total_users: 0,
    total_amount: 0,
    total_qnt: 0,
    status: [],
    status_all: [],
    user_unsubscribe: [],
    user_unsubscribe_all: [],
    user_without_unsubscribe: [],
    user_without_unsubscribe_all: [],
    city: [],
    sex: [],
    country: [],
    profile_type: [],
    barChartAge: {
      barChart: [],
      maxValue: 0,
      barChangeLine: [],
      tableData: [],
    },
    topCountry: [],
    conventionType: [],
    points: [],
    transactionType: {
      barChart: [],
      maxValue: 0,
      barChangeLine: [],
      tableData: [],
    },
    transactionTypeAll: {
      barChart: [],
      maxValue: 0,
      barChangeLine: [],
      tableData: [],
    },
    transactionInfo: {
      barChart: [],
      maxValue: 0,
      barChangeLine: [],
      tableData: [],
    },
    moreFiveThousand: [],
    companyAmount: [],
  });

  const [data, setData] = useState(defaultState);
  const [filters, setFilters] = useState([
    {
      title: t('Periodo'),
      type: 'range',
      name: 'date_to',
      format: 'DD-MM-YYYY',
      keys: ['date_from', 'date_to'],
      names: ['date_from', 'date_to'],
    },
    {
      title: t('Outlet Village'),
      type: 'select',
      name: 'by_shopping_center',
      values: shoppingCenters.map((i) => ({
        value: i.id,
        title: i.title,
      })),
    },
  ]);

  useEffect(() => {
    if (shoppingCenters) {
      setFilters((s) => {
        return s.map((f) => {
          if (f.name === 'by_shopping_center') {
            return {
              ...f,
              values: shoppingCenters.map((i) => ({
                value: i.id,
                title: i.title,
              })),
            };
          }
          return f;
        });
      });
    }
  }, [shoppingCenters]);

  const handleDownloadExcel = async () => {
    setLoading(true);
    try {
      const params = {
        ...Object.fromEntries([...searchParams]),
      };
      const response = await api.getExcel({ params });
      Helpers.downloadFile(response, fileNameEdit());
    } catch (e) {
      const error = e?.response?.data?.errors || [t('Something went wrong!')];
      UIHelper.openNotificationWithIcon('error', error.join('\n'));
    } finally {
      setLoading(false);
    }
  };

  const columsForMoreFiveThousand = useMemo(
    () => [
      {
        title: t('BRAND'),
        dataIndex: 'name',
      },
      // {
      //   title: t('ID_TRANSAZIONE'),
      //   dataIndex: 'transaction_id',
      // },
      {
        title: t('PUNTI'),
        dataIndex: 'amount',
        render: (v) => Helpers.numberWithCommas(v),
      },
      {
        title: t('DATA_TRANSAZIONE'),
        dataIndex: 'date',
        render: (v) => moment(v).format('DD-MMM-YYYY'),
      },
    ],
    [t],
  );

  const columsForCompanyAmount = useMemo(
    () => [
      {
        title: t('Tenant'),
        dataIndex: 'name',
      },
      // {
      //   title: t('Scontrini'),
      //   dataIndex: 'receipts',
      //   render: (v) => Helpers.numberWithCommas(v),
      // },
      {
        title: t('Punti erogati'),
        dataIndex: 'points',
        render: (v) => Helpers.numberWithCommas(v),
      },
      // {
      //   title: t('ScM Tenant'),
      //   dataIndex: 'avg',
      //   render: (v) => Helpers.numberWithCommas(`${v}€`),
      // },
    ],
    [t],
  );

  const columsForRegistration = useMemo(
    () => [
      {
        title: t('Touch Point'),
        dataIndex: 'attr',
      },
      // {
      //   title: t('Scontrini'),
      //   dataIndex: 'receipts',
      //   render: (v) => Helpers.numberWithCommas(v),
      // },
      {
        title: t('# registrati'),
        dataIndex: 'qnt',
        render: (v) => Helpers.numberWithCommas(v),
      },
      // {
      //   title: t('ScM Tenant'),
      //   dataIndex: 'avg',
      //   render: (v) => Helpers.numberWithCommas(`${v}€`),
      // },
    ],
    [t],
  );

  const columsForPoints = useMemo(
    () => [
      {
        title: t('Soglie'),
        dataIndex: 'key',
      },
      {
        title: t('# clienti'),
        dataIndex: 'value',
        render: (v) => Helpers.numberWithCommas(v),
      },
      {
        title: t('% clienti'),
        dataIndex: 'percent',
        render: (v) => Helpers.numberWithCommas(format(v)),
      },
    ],
    [t],
  );

  const columsForConventionType = useMemo(
    () => [
      {
        title: t('Presenza convenzione'),
        dataIndex: 'name',
        render: (v) => v.toUpperCase(),
      },
      {
        title: t('# clienti'),
        dataIndex: 'qnt',
        render: (v) => Helpers.numberWithCommas(v),
      },
      {
        title: t('% clienti'),
        dataIndex: 'percent',
        render: (v) => Helpers.numberWithCommas(format(v)),
      },
    ],
    [t],
  );

  const columsForStatus = useMemo(
    () => [
      {
        title: '',
        dataIndex: 'attr',
        width: 300,
        render: (v, r) => `${v.toUpperCase()} (${r.sub_title || ''})`,
      },
      {
        title: t('# registrati'),
        dataIndex: 'qnt',
        render: (v) => Helpers.numberWithCommas(v),
      },
      {
        title: t('% registrati'),
        dataIndex: 'percent',
        render: (v) => Helpers.numberWithCommas(format(v)),
      },
      {
        title: t('# user_without_unsubscribe'),
        dataIndex: 'without_unsubscribe_qnt',
        render: (v) => Helpers.numberWithCommas(v),
      },
      {
        title: t('% user_without_unsubscribe'),
        dataIndex: 'without_unsubscribe_percent',
        render: (v) => Helpers.numberWithCommas(format(v)),
      },
      {
        title: t('# user_unsubscribe'),
        dataIndex: 'unsubscribe_qnt',
        render: (v) => Helpers.numberWithCommas(v),
      },
    ],
    [t],
  );

  const columsForCity = useMemo(
    () => [
      {
        title: t('Provincia'),
        dataIndex: 'attr',
        render: (v) => (!v ? t('Missing') : v.toUpperCase()),
      },
      {
        title: t('# registrati'),
        dataIndex: 'qnt',
        render: (v) => Helpers.numberWithCommas(v),
      },
      {
        title: t('% registrati'),
        dataIndex: 'percent',
        render: (v) => Helpers.numberWithCommas(format(v)),
      },
    ],
    [t],
  );

  const columsForProfileType = useMemo(
    () => [
      {
        title: t('Profilo'),
        dataIndex: 'key',
        render: (v) => v.toUpperCase(),
      },
      {
        title: t('# registrati'),
        dataIndex: 'value',
        render: (v) => Helpers.numberWithCommas(v),
      },
      {
        title: t('% registrati'),
        dataIndex: 'percent',
        render: (v) => Helpers.numberWithCommas(format(v)),
      },
    ],
    [t],
  );

  const columsForTopCountry = useMemo(
    () => [
      {
        title: t('Nazinalita'),
        dataIndex: 'attr',
        render: (v) => (!v ? t('Missing') : v.toUpperCase()),
      },
      {
        title: t('# registrati'),
        dataIndex: 'qnt',
        render: (v) => Helpers.numberWithCommas(v),
      },
      {
        title: t('% registrati'),
        dataIndex: 'percent',
        render: (v) => Helpers.numberWithCommas(format(v)),
      },
    ],
    [t],
  );

  const [columns, setColumns] = useState([
    {
      title: t('Periodo'),
      dataIndex: 'Periodo',
      fixed: true,
    },
    {
      title: t('Footfall vs LY (+/-%)'),
      dataIndex: 'Footfall vs LY (+/-%)',
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
    {
      title: t('Incidenza % Footfall YTD'),
      dataIndex: 'Incidenza % Footfall YTD',
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
    {
      title: t('Incidenza % FootfallLY'),
      dataIndex: 'Incidenza % Footfall LY',
      render: (v) => Helpers.changeDotToComa(format(v)),
    },
  ]);

  const summary = useCallback(() => {
    const { total = {} } = data;

    delete total.Periodo;

    const tableKeys = Object.keys(total);
    const filteredColumns = columns.filter((column) =>
      tableKeys.includes(column.dataIndex),
    );

    return (
      <Table.Summary fixed>
        <Table.Summary.Row fixed>
          <Table.Summary.Cell index={0}>
            <Text strong>{t('Totale')}</Text>
          </Table.Summary.Cell>
          {filteredColumns.map(({ dataIndex, render = (v) => v }) => (
            <Table.Summary.Cell kry={dataIndex}>
              <Text strong>{render(total[dataIndex])}</Text>
            </Table.Summary.Cell>
          ))}
        </Table.Summary.Row>
      </Table.Summary>
    );
  }, [data]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const objSP = Object.fromEntries([...searchParams]);
        console.log('objSP', objSP);
        const params = {
          date_from: objSP.date_from,
          date_to: objSP.date_to,
          by_shopping_center: objSP.by_shopping_center,
        };

        const { data } = await api.getFootfalls({ params });

        console.log('data', data);
        const {
          profile_type = [],
          age = [],
          country = [],
          sex = [],
          city = [],
          status = [],
          status_all = [],
          convention_type = [],
          total_users = 0,
          total_qnt = 0,
          total_amount = 0,
          points: { data: pointsData = [] } = {},
          transaction_type: { data: transactionTypeData = [] } = {},
          transaction_full = [],
          transaction_infopoint = [],
          more_five_thousand = [],
          touch_point = [],
          touch_point_all = [],
          company_amount = [],
          user_unsubscribe = [],
          user_unsubscribe_all = [],
          user_without_unsubscribe = [],
          user_without_unsubscribe_all = [],
        } = data;

        const barChartType = transactionTypeData.map((i) => ({
          date: t(i.name),
          Age: i.percent_qnt,
          AgeColor: '#61cdbb',
        }));

        const barChartAge = age.map((i) => ({
          date: i.key === 'other' ? t('Missing') : i.key,
          Age: i.percent,
          AgeColor: '#61cdbb',
        }));
        const barChartTransactionAll = transaction_full.map((i) => ({
          date: t(i.key),
          Age: i.value,
          AgeColor: '#61cdbb',
        }));
        const barChartTransactionInfo = transaction_infopoint.map((i) => ({
          date: i.key === 'SCONTRINO' ? t(`${i.key}_2`) : t(i.key),
          Age: i.value,
          AgeColor: '#61cdbb',
        }));

        const colorsSex = {
          female: 'hsl(56, 70%, 50%)',
          male: 'hsl(46, 70%, 50%)',
          other: 'hsl(200, 70%, 50%)',
        };

        setAllData({
          total_users: total_users,
          total_amount,
          total_qnt,
          status,
          status_all,
          user_unsubscribe,
          user_unsubscribe_all,
          user_without_unsubscribe,
          user_without_unsubscribe_all,
          profile_type,
          country,
          city,
          conventionType: convention_type,
          points: pointsData,
          moreFiveThousand: more_five_thousand,
          companyAmount: company_amount,
          touchPoint: touch_point,
          touchPointAll: touch_point_all,
          transactionType: {
            barChart: barChartType,
            maxValue: Helpers.getMaxValuFromArrayOfObject(
              transactionTypeData,
              'percent_qnt',
            ),
            barChangeLine: [],
            tableData: transactionTypeData?.map(i => ({
              ...i,
              name: t(i.name),
            })),
          },
          transactionTypeAll: {
            barChart: barChartTransactionAll,
            maxValue: Helpers.getMaxValuFromArrayOfObject(
              transaction_full,
              'value',
            ),
            barChangeLine: [],
            tableData: transaction_full?.map(i => ({
              ...i,
              name: t(i.key),
            })),
          },
          transactionInfo: {
            barChart: barChartTransactionInfo,
            maxValue: Helpers.getMaxValuFromArrayOfObject(
              transaction_infopoint,
              'value',
            ),
            barChangeLine: [],
            tableData: transaction_infopoint?.map(i => ({
              ...i,
              name: t(i.key),
            })),
          },
          sex: sex.map((i) => ({
            id: t(i.attr),
            label: t(i.attr),
            value: i.percent,
            count: i.qnt,
            color: colorsSex[i.attr],
          })),
          barChartAge: {
            barChart: barChartAge,
            maxValue: Helpers.getMaxValuFromArrayOfObject(age, 'percent'),
            barChangeLine: [],
            tableData: age,
          },
        });

        // const tableKeys = Object.keys(total);
        // const filteredColumns = columns.filter((column) =>
        //   tableKeys.includes(column.dataIndex),
        // );
        // const lineChartData1 = {
        //   id: 'Footfall LY',
        //   // color: 'blue',
        //   data: data.map((item) => ({
        //     x: item['Periodo'],
        //     y: item['Footfall LY'],
        //   })),
        // };
        // const lineChartData2 = {
        //   id: 'Footfall YTD',
        //   // color: 'blue',
        //   data: data.map((item) => ({
        //     x: item['Periodo'],
        //     y: item['Footfall YTD'],
        //   })),
        // };
        //
        // const maxValueFroLy = _.maxBy(data, 'Incidenza % Footfall LY')[
        //   'Incidenza % Footfall LY'
        // ];
        // const maxValueFroYtd = _.maxBy(data, 'Incidenza % Footfall YTD')[
        //   'Incidenza % Footfall YTD'
        // ];
        //
        // const maxValue =
        //   maxValueFroLy > maxValueFroYtd ? maxValueFroLy : maxValueFroYtd;
        //
        //
        // const barChangeLine = {
        //   id: 'Footfall vs LY',
        //   data: data.map((item) => ({
        //     x: item['Periodo'],
        //     y: item['Footfall vs LY (+/-%)'],
        //     tooltipKeys: [
        //       {
        //         key: 'Footfall LY',
        //         value: Helpers.changeDotToComa(
        //           format(item['Incidenza % Footfall LY']),
        //         ),
        //         color: '#61cdbb',
        //         percent: '',
        //       },
        //       {
        //         key: 'Footfall YTD',
        //         value: Helpers.changeDotToComa(
        //           format(item['Incidenza % Footfall YTD']),
        //         ),
        //         color: '#f47560',
        //         percent: '',
        //       },
        //       {
        //         key: 'Footfall vs LY',
        //         value: Helpers.changeDotToComa(
        //           format(item['Footfall vs LY (+/-%)']),
        //         ),
        //         color: '#1f77b4',
        //         percent: '',
        //       },
        //     ],
        //   })),
        // };
        //
        // setData({
        //   tableData: data,
        //   lineChartData: [lineChartData1, lineChartData2],
        //   barChart,
        //   maxValue,
        //   barChangeLine: [barChangeLine],
        //   total,
        // });
        // setColumns(filteredColumns);
        // setErrors([]);
      } catch (e) {
        const error = e?.response?.data?.date || [];
        if (error.length) {
          UIHelper.openNotificationWithIcon('error', error.join(', '));
        }
        setErrors(UIHelper.showAnalyticsError(e));
        console.log(e);
      } finally {
        setLoading(false);
        if (isPrint) {
          createCustomEvent();
        }
      }
    })();
  }, [searchParams, isPrint, isAllReports, allReportsData]);

  const mergeArrays = (statusArray, unsubscribeArray, withoutUnsubscribeArray) => {
    const mergedArray = statusArray.map((item, index) => {
      const newItem = { ...item };

      newItem.unsubscribe_qnt = unsubscribeArray[index].qnt || 0;

      newItem.without_unsubscribe_qnt = withoutUnsubscribeArray[index].qnt || 0;
      newItem.without_unsubscribe_percent = withoutUnsubscribeArray[index].percent || 0;

      return newItem;
    });

    return mergedArray;
  };

  return {
    //
    columsForProfileType,
    allData,
    columsForTopCountry,
    columsForCity,
    columsForStatus,
    columsForConventionType,
    columsForPoints,
    columsForMoreFiveThousand,
    columsForCompanyAmount,
    columsForRegistration,
    //

    columns,
    data,
    filters,
    title,
    searchParams,
    format,
    loading,
    handleDownloadExcel,
    summary,
    handleDownloadPdf,
    loadingPdf,
    errors,
    mergeArrays,
  };
};

export default useVipClub;
